import { useState } from 'react';

import { Button } from '../cdl/Button/Button';
import { Callout } from '../cdl/Callout/Callout';
import { Checkbox } from '../cdl/Checkbox/Checkbox';
import { DatePicker } from '../cdl/DatePicker/DatePicker';
import { Input } from '../cdl/Input/Input';
import { Text } from '../cdl/Text/Text';
import { useToasts } from '../cdl/Toast/useToasts';
import { confirmOffer, exportOffer, importPurchaseRequest } from '../common/api/clients/glsm.api';
import { translate } from '../common/helpers/translate.helper';
import { IconInfoCircle } from '../common/icons/cdl/InfoCircle';
import { InfoPopover } from '../common/InfoPopover/InfoPopover';
import { Box } from '../common/ui/Box';

import { IntegrationOfferSelect } from './components/IntegrationOfferSelect';

export const GlsmPageContent = () => {
    return (
        <div>
            <Callout
                heading={translate('integrationsOverview.calloutGlsm.headline')}
                description={translate('integrationsOverview.calloutGlsm.description')}
                Icon={IconInfoCircle}
            />
            <Box display="grid" rowGap={40} maxWidth="400px" marginTop={24}>
                <ExportSection />
                <ConfirmSection />
                <ImportSection />
            </Box>
        </div>
    );
};

const ExportSection = () => {
    const [state, setState] = useState({
        offerId: '',
        skipMariAppsVesselValidation: false,
    });
    const { addToast } = useToasts();

    return (
        <Box display="flex" gap={8} flexDirection="column" alignItems="flex-start">
            <Text variant="title" as="h2">
                {translate('integrationsOverview.exportTitle')}
            </Text>

            <Text variant="small" color="foreground.subtle">
                {translate('integrationsOverview.exportDescription')}
            </Text>

            <Box width="100%">
                <IntegrationOfferSelect
                    label={translate('integrationsOverview.selectLabel')}
                    value={state.offerId}
                    onChange={(option) =>
                        setState({
                            offerId: option.value,
                            skipMariAppsVesselValidation: state.skipMariAppsVesselValidation,
                        })
                    }
                />
            </Box>

            <Checkbox
                checked={state.skipMariAppsVesselValidation}
                onChange={(event) =>
                    setState({
                        ...state,
                        skipMariAppsVesselValidation: event.target.checked,
                    })
                }
                label={
                    <Box display="flex" alignItems="center" gap={4}>
                        <Text variant="body">
                            {translate('integrationsOverview.skipMariAppsVesselValidationLabel')}
                        </Text>
                        <InfoPopover width="420px" side="right" align="start">
                            <Text variant="small">
                                {translate('integrationsOverview.skipMariAppsVesselValidationDescription')}
                            </Text>
                        </InfoPopover>
                    </Box>
                }
            />

            <Button
                disabled={!state.offerId}
                onClick={() => {
                    exportOffer({
                        offerId: state.offerId,
                        skipMariAppsVesselValidation: state.skipMariAppsVesselValidation,
                    });
                    addToast(translate('integrationsOverview.exportTriggered'));
                }}
            >
                {translate('integrationsOverview.exportButtonText')}
            </Button>
        </Box>
    );
};

const ConfirmSection = () => {
    const [offerId, setOfferId] = useState('');
    const [customerId, setCustomerId] = useState('');
    const { addToast } = useToasts();

    return (
        <Box display="flex" gap={8} flexDirection="column" alignItems="flex-start">
            <Text variant="title" as="h2">
                {translate('integrationsOverview.confirmTitle')}
            </Text>

            <Box width="100%">
                <IntegrationOfferSelect
                    label={translate('integrationsOverview.selectLabel')}
                    value={offerId}
                    onChange={(option) => {
                        setOfferId(option.value);
                        setCustomerId(option.offer.customerId);
                    }}
                />
            </Box>

            <Button
                disabled={!offerId}
                onClick={() => {
                    confirmOffer(offerId, customerId);
                    addToast(translate('integrationsOverview.confirmTriggered'));
                }}
            >
                {translate('integrationsOverview.confirmButtonText')}
            </Button>
        </Box>
    );
};

const ImportSection = () => {
    const [purchaseRequestCode, setPurchaseRequestCode] = useState('');
    const [date, setDate] = useState<Date | undefined>(undefined);
    const { addToast } = useToasts();

    return (
        <Box display="flex" gap={6} flexDirection="column" alignItems="flex-start">
            <Text variant="title" as="h2">
                {translate('integrationsOverview.importTitle')}
            </Text>

            <Box width="100%">
                <DatePicker
                    label={translate('integrationsOverview.datePicker')}
                    date={date}
                    onDateChange={(date) => setDate(date)}
                />

                <Input
                    label={translate('integrationsOverview.code')}
                    value={purchaseRequestCode}
                    onChange={(e) => setPurchaseRequestCode(e.target.value)}
                />
            </Box>

            <Button
                disabled={!purchaseRequestCode || !date}
                emphasis="medium"
                onClick={() => {
                    importPurchaseRequest(purchaseRequestCode, date || new Date());
                    addToast(translate('integrationsOverview.importTriggered'));
                }}
            >
                {translate('integrationsOverview.importButtonText')}
            </Button>
        </Box>
    );
};
